import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import VehicleModal from './VehicleModal';
import TestDriveScheduler from './TestDriveScheduler';

const { FiHeart, FiMessageSquare, FiEye, FiFilter, FiCalendar } = FiIcons;

function InventoryShowcase() {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showTestDrive, setShowTestDrive] = useState(false);
  const [likedVehicles, setLikedVehicles] = useState(new Set());

  const vehicles = [
    {
      id: 1,
      make: 'Audi',
      model: 'Q5 Quattro',
      year: 2023,
      price: 54999,
      mileage: 12500,
      type: 'SUV',
      image: 'https://images.unsplash.com/photo-1544636331-e26879cd4d9b?w=400&h=300&fit=crop',
      features: ['Quattro AWD', 'Premium Plus', 'Navigation', 'Safety Package']
    },
    {
      id: 2,
      make: 'BMW',
      model: 'X3 xDrive',
      year: 2022,
      price: 48999,
      mileage: 18200,
      type: 'SUV',
      image: 'https://images.unsplash.com/photo-1552519507-da3b142c6e3d?w=400&h=300&fit=crop',
      features: ['xDrive', 'M Sport', 'Harman Kardon', 'Premium Package']
    },
    {
      id: 3,
      make: 'Mercedes',
      model: 'C-Class',
      year: 2023,
      price: 42999,
      mileage: 8500,
      type: 'Sedan',
      image: 'https://images.unsplash.com/photo-1618843479313-40f8afb4b4d8?w=400&h=300&fit=crop',
      features: ['AMG Line', 'MBUX', 'Premium Audio', '4MATIC AWD']
    },
    {
      id: 4,
      make: 'Tesla',
      model: 'Model Y',
      year: 2023,
      price: 52999,
      mileage: 5200,
      type: 'Electric',
      image: 'https://images.unsplash.com/photo-1617788138017-80ad40651399?w=400&h=300&fit=crop',
      features: ['Autopilot', 'Long Range', 'Premium Interior', 'Supercharger Access']
    }
  ];

  const categories = ['All', 'SUV', 'Sedan', 'Electric', 'Luxury'];

  const filteredVehicles = selectedCategory === 'All' 
    ? vehicles 
    : vehicles.filter(vehicle => vehicle.type === selectedCategory);

  const toggleLike = (vehicleId) => {
    const newLiked = new Set(likedVehicles);
    if (newLiked.has(vehicleId)) {
      newLiked.delete(vehicleId);
    } else {
      newLiked.add(vehicleId);
    }
    setLikedVehicles(newLiked);
  };

  const openVehicleModal = (vehicle) => {
    setSelectedVehicle(vehicle);
    setShowModal(true);
  };

  const scheduleTestDrive = (vehicle) => {
    setSelectedVehicle(vehicle);
    setShowTestDrive(true);
  };

  return (
    <section id="inventory" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Halifax Featured Inventory
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Discover our curated selection of premium vehicles with AI-powered recommendations for Maritime drivers.
          </p>
        </motion.div>

        {/* Category Filter */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="flex flex-wrap justify-center gap-4 mb-12"
        >
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-6 py-3 rounded-full font-medium transition-all ${
                selectedCategory === category
                  ? 'bg-blue-600 text-white shadow-lg'
                  : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
              }`}
            >
              {category}
            </button>
          ))}
        </motion.div>

        {/* AI Recommendation Banner */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="bg-gradient-to-r from-blue-50 to-indigo-100 rounded-xl p-6 mb-8 border border-blue-200"
        >
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center">
              <SafeIcon icon={FiMessageSquare} className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Halifax AI Recommendation</h3>
              <p className="text-gray-600">
                Based on Maritime winter conditions, here are AWD vehicles perfect for Halifax driving.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Vehicle Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredVehicles.map((vehicle, index) => (
            <motion.div
              key={vehicle.id}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="bg-white rounded-xl shadow-lg overflow-hidden hover-lift"
            >
              <div className="relative">
                <img
                  src={vehicle.image}
                  alt={`${vehicle.year} ${vehicle.make} ${vehicle.model}`}
                  className="w-full h-48 object-cover cursor-pointer"
                  onClick={() => openVehicleModal(vehicle)}
                />
                <button
                  onClick={() => toggleLike(vehicle.id)}
                  className={`absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                    likedVehicles.has(vehicle.id)
                      ? 'bg-red-500 text-white'
                      : 'bg-white/90 text-gray-600 hover:bg-white'
                  }`}
                >
                  <SafeIcon icon={FiHeart} className="w-4 h-4" />
                </button>
              </div>
              <div className="p-6">
                <div className="flex justify-between items-start mb-2">
                  <h3 className="text-lg font-semibold text-gray-900 cursor-pointer hover:text-blue-600"
                      onClick={() => openVehicleModal(vehicle)}>
                    {vehicle.year} {vehicle.make} {vehicle.model}
                  </h3>
                  <span className="text-sm bg-blue-100 text-blue-600 px-2 py-1 rounded">
                    {vehicle.type}
                  </span>
                </div>
                <p className="text-gray-600 text-sm mb-3">
                  {vehicle.mileage.toLocaleString()} km
                </p>
                <div className="space-y-2 mb-4">
                  {vehicle.features.slice(0, 2).map((feature, idx) => (
                    <span
                      key={idx}
                      className="inline-block text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded mr-1"
                    >
                      {feature}
                    </span>
                  ))}
                </div>
                <div className="flex justify-between items-center mb-4">
                  <div className="text-2xl font-bold text-blue-600">
                    ${vehicle.price.toLocaleString()} CAD
                  </div>
                  <div className="text-sm text-gray-500">
                    Est. $787/mo
                  </div>
                </div>
                <div className="space-y-2">
                  <button
                    onClick={() => openVehicleModal(vehicle)}
                    className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2"
                  >
                    <SafeIcon icon={FiEye} className="w-4 h-4" />
                    <span>View Details</span>
                  </button>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => scheduleTestDrive(vehicle)}
                      className="flex-1 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center space-x-1"
                    >
                      <SafeIcon icon={FiCalendar} className="w-3 h-3" />
                      <span className="text-sm">Test Drive</span>
                    </button>
                    <button className="flex-1 px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center justify-center space-x-1">
                      <SafeIcon icon={FiMessageSquare} className="w-3 h-3" />
                      <span className="text-sm">Ask AI</span>
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Load More */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="text-center mt-12"
        >
          <button className="px-8 py-3 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors flex items-center space-x-2 mx-auto">
            <SafeIcon icon={FiFilter} className="w-5 h-5" />
            <span>View All Halifax Inventory ({vehicles.length + 20}+ vehicles)</span>
          </button>
        </motion.div>
      </div>

      {/* Modals */}
      <VehicleModal
        vehicle={selectedVehicle}
        isOpen={showModal}
        onClose={() => setShowModal(false)}
      />
      
      <TestDriveScheduler
        vehicle={selectedVehicle}
        isOpen={showTestDrive}
        onClose={() => setShowTestDrive(false)}
      />
    </section>
  );
}

export default InventoryShowcase;